import { zodResolver } from "@hookform/resolvers/zod";
import { usePreventScroll } from "@react-aria/overlays";
import { AHB_WITHDRAW_REASON } from "core/consts";
import { AHBWithdrawFormValues } from "core/types";
import { ConfirmationDialogProps } from "ds_legacy/components/ConfirmationDialog/useConfirmationDialog";
import {
  Button,
  ControlledRadioGroup,
  ControlledTextarea,
  Form,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  type RadioGroupOption,
} from "ds/ui";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "translations";
import { useGetTranslateWithdrawReason } from "./useAHBMessageSection";
import { getDefaultValues } from "./withdrawMessageReason/getDefaultValues";
import {
  createWithdrawMessageReasonSchema,
  type WithdrawMessageReasonFormValues,
} from "./withdrawMessageReason/schema";

export type WithdrawMessageReasonFormProps = Pick<
  ConfirmationDialogProps<AHBWithdrawFormValues>,
  "onCancel" | "opened" | "onSubmit" | "id"
>;

export function WithdrawMessageReasonForm({
  id,
  onCancel,
  onSubmit,
  opened,
}: WithdrawMessageReasonFormProps) {
  usePreventScroll({ isDisabled: !opened });
  const translations = useTranslations();
  const getTranslateWithdrawReason = useGetTranslateWithdrawReason();
  const schema = useMemo(
    () => createWithdrawMessageReasonSchema(translations),
    [translations],
  );

  const methods = useForm<WithdrawMessageReasonFormValues>({
    defaultValues: getDefaultValues(),
    resolver: zodResolver(schema),
  });

  const withdrawReason = useWatch({
    control: methods.control,
    name: "withdraw_reason",
  });
  const previousWithdrawReasonRef = useRef(withdrawReason);

  useEffect(() => {
    if (
      previousWithdrawReasonRef.current !== undefined &&
      previousWithdrawReasonRef.current !== withdrawReason
    ) {
      methods.setValue("withdraw_reason_other", "", { shouldValidate: false });
    }
    previousWithdrawReasonRef.current = withdrawReason;
  }, [methods, withdrawReason]);

  const radioOptions = useMemo((): RadioGroupOption<string>[] => {
    const otherPlaceholder =
      translations.insuranceApp.eligibilityTab.sendDocuments.withdrawModal
        .otherPlaceholder;

    return [
      {
        label: getTranslateWithdrawReason(
          AHB_WITHDRAW_REASON.PATIENT_DECLINED_REHAB,
        ),
        value: AHB_WITHDRAW_REASON.PATIENT_DECLINED_REHAB,
      },
      {
        label: getTranslateWithdrawReason(
          AHB_WITHDRAW_REASON.NOT_REHAB_CAPABLE,
        ),
        value: AHB_WITHDRAW_REASON.NOT_REHAB_CAPABLE,
      },
      {
        label: getTranslateWithdrawReason(AHB_WITHDRAW_REASON.NOT_PROMISING),
        value: AHB_WITHDRAW_REASON.NOT_PROMISING,
      },
      {
        label: getTranslateWithdrawReason(AHB_WITHDRAW_REASON.PATIENT_DECEASED),
        value: AHB_WITHDRAW_REASON.PATIENT_DECEASED,
      },
      {
        label: getTranslateWithdrawReason(AHB_WITHDRAW_REASON.OTHER),
        subForm: ({ isDisabled, size }) => (
          <ControlledTextarea
            isDisabled={isDisabled}
            isRequired
            label={otherPlaceholder}
            maxLength={600}
            minRows={3}
            name="withdraw_reason_other"
            placeholder={otherPlaceholder}
            size={size}
          />
        ),
        value: AHB_WITHDRAW_REASON.OTHER,
      },
    ];
  }, [getTranslateWithdrawReason, translations]);

  function handleClose() {
    onCancel?.();
  }

  const handleValidSubmit = methods.handleSubmit(
    (values: WithdrawMessageReasonFormValues) => {
      onSubmit?.(values as AHBWithdrawFormValues);
    },
  );

  const withdrawTitle =
    translations.insuranceApp.eligibilityTab.sendDocuments.withdrawModal
      .withdrawTitle;

  return (
    <Modal
      data-testid={id}
      id={id}
      isOpen={opened}
      size="lg"
      onClose={handleClose}
    >
      <ModalContent>
        <FormProvider {...methods}>
          <Form className="gap-0" onSubmit={handleValidSubmit}>
            <ModalHeader className="flex flex-col gap-1 pb-2">
              {withdrawTitle}
            </ModalHeader>
            <ModalBody className="gap-4 py-0">
              <ControlledRadioGroup
                isRequired
                label={
                  translations.insuranceApp.eligibilityTab.sendDocuments
                    .withdrawModal.withdrawSubtitle
                }
                name="withdraw_reason"
                options={radioOptions}
                orientation="vertical"
                size="md"
              />
            </ModalBody>
            <ModalFooter className="w-full justify-end gap-3">
              <Button
                color="primary"
                id="cancel-withdrawal"
                trackEventProps={{ withdraw_reason: withdrawReason }}
                type="button"
                variant="light"
                onPress={handleClose}
              >
                {translations.actions.cancel}
              </Button>
              <Button
                color="primary"
                id="confirm-withdrawal"
                trackEventProps={{ withdraw_reason: withdrawReason }}
                type="submit"
                variant="solid"
              >
                {translations.actions.confirm}
              </Button>
            </ModalFooter>
          </Form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
}
